import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { emailCampaignsApi } from '../api/emailCampaignsApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type { CreateEmailCampaignPayload } from '../types';

export const useEmailCampaigns = (page: number, limit = 20) =>
  useQuery({
    queryKey: ['email-campaigns', page, limit],
    queryFn: () => emailCampaignsApi.list(page, limit),
    staleTime: 60 * 1000,
  });

/**
 * Delivery logs for one campaign. Enabled only once a campaign is selected, so
 * opening the page doesn't fire a request for a route that needs an id.
 */
export const useEmailCampaignLogs = (campaignId: string | null) =>
  useQuery({
    queryKey: ['email-campaigns', campaignId, 'logs'],
    queryFn: () => emailCampaignsApi.getLogs(campaignId as string),
    enabled: !!campaignId,
    // Deliveries are enqueued asynchronously by PE-891, so a campaign opened
    // right after sending will still be filling in. Short staleness + an explicit
    // refresh control beats polling a list that is usually already final.
    staleTime: 15 * 1000,
  });

export const useCreateEmailCampaign = () => {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: CreateEmailCampaignPayload) => emailCampaignsApi.create(payload),
    onSuccess: (campaign) => {
      // The BFF records the campaign itself, so the list is stale the moment this
      // returns. Logs are keyed per campaign and this one is brand new, so only
      // the list needs invalidating.
      qc.invalidateQueries({ queryKey: ['email-campaigns'] });
      toast.success(
        `"${campaign.title}" queued for ${campaign.recipientCount} recipient${
          campaign.recipientCount === 1 ? '' : 's'
        }`,
      );
    },
    onError: (err: unknown) => {
      // The shared fetch layer already toasts server-returned errors and marks
      // them `toasted`; only surface what it couldn't.
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error('Could not send the campaign. Please check your connection and try again.');
      }
    },
  });
};
